import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Search, Package, User, FileText, Settings, X, Plus, LogOut, DollarSign, Home, LayoutDashboard, Briefcase, Command, Users, MessageSquare, Bell, Star, BarChart3, Target, FolderOpen } from 'lucide-react';
import axios from 'axios';

const CommandCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Toggle Command Center via Ctrl+K / Cmd+K or Custom Event
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    const handleOpenEvent = () => setIsOpen(true);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('open_command_center', handleOpenEvent);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('open_command_center', handleOpenEvent);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery('');
      setSelectedIndex(0);
      setResults(getDefaultActions());
    }
  }, [isOpen]);

  // Fetch search results when query changes
  useEffect(() => {
    if (!isOpen) return;
    
    if (query.trim().length === 0) {
      setResults(getDefaultActions());
      setSelectedIndex(0);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        // 1. Filter Static Actions locally
        const staticResults = getDefaultActions().filter(action => 
          action.title.toLowerCase().includes(query.toLowerCase()) || 
          action.subtitle?.toLowerCase().includes(query.toLowerCase()) ||
          action.keywords?.some(k => k.toLowerCase().includes(query.toLowerCase()))
        );

        // 2. Multi-Entity Parallel Fetch
        const [projRes, uRes, invRes] = await Promise.all([
          user ? axios.get(`/api/projects?search=${encodeURIComponent(query)}&limit=5`).catch(() => null) : null,
          isAdmin ? axios.get(`/api/users?search=${encodeURIComponent(query)}&limit=5`).catch(() => null) : null,
          user ? axios.get(`/api/invoices?search=${encodeURIComponent(query)}&limit=5`).catch(() => null) : null
        ]);

        // 3. Process Results
        const projectResults = (projRes?.data?.data?.projects || []).map(p => {
          const q = query.toLowerCase();
          const matchedM = p.milestones?.find(m => m.title.toLowerCase().includes(q));
          const matchedA = p.finalAssets?.find(a => a.title.toLowerCase().includes(q));

          return {
            id: `proj-${p._id}`,
            title: p.title,
            subtitle: matchedM 
              ? `Milestone: ${matchedM.title}` 
              : (matchedA ? `Asset: ${matchedA.title}` : `Project • ${p.status}`),
            icon: <Package className="w-4 h-4 text-blue-500" />,
            action: () => navigate(`/projects/${p._id}`)
          };
        });

        const userResults = (uRes?.data?.data?.users || []).map(u => ({
          id: `user-${u._id}`,
          title: u.name,
          subtitle: `User • ${u.role} • ${u.company || 'Private'}`,
          icon: <User className="w-4 h-4 text-amber-500" />,
          action: () => navigate('/admin-users') // Or a specific user profile if it exists
        }));

        const invoiceResults = (invRes?.data?.data?.invoices || []).map(inv => ({
          id: `inv-${inv._id}`,
          title: `Invoice ${inv.invoiceNumber}`,
          subtitle: `Billing • ${inv.status.toUpperCase()} • $${inv.total}`,
          icon: <FileText className="w-4 h-4 text-emerald-500" />,
          action: () => navigate(isAdmin ? '/admin-billing' : '/billing')
        }));

        setResults([...projectResults, ...userResults, ...invoiceResults, ...staticResults].slice(0, 12));
        setSelectedIndex(0);
      } catch (err) {
        console.error("Global search error:", err);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, isOpen, user, navigate]);

  const getDefaultActions = () => {
    if (!user) {
      return [
        { id: '1', title: 'Home', icon: <Home className="w-4 h-4 text-blue-500" />, action: () => navigate('/'), keywords: ['landing', 'main'] },
        { id: '2', title: 'Services', icon: <Briefcase className="w-4 h-4 text-emerald-500" />, action: () => navigate('/services'), keywords: ['what we do', 'offerings'] },
        { id: '3', title: 'Client Portal Login', icon: <User className="w-4 h-4 text-amber-500" />, action: () => navigate('/login'), keywords: ['signin', 'auth'] }
      ];
    }

    const baseActions = [
      { id: 'dash', title: 'Dashboard', subtitle: 'Return to Hub', icon: <LayoutDashboard className="w-4 h-4 text-indigo-500" />, action: () => navigate('/dashboard'), keywords: ['home', 'main', 'hub'] },
      { id: 'profile', title: 'My Profile', subtitle: 'Account settings', icon: <User className="w-4 h-4 text-gray-500" />, action: () => navigate('/profile'), keywords: ['me', 'account', 'identity'] },
    ];

    // Client Tools (Always available to Clients, also available to Admins for full visibility)
    const clientTools = [
      { id: 'my-projects', title: 'My Projects', icon: <FolderOpen className="w-4 h-4 text-blue-500" />, action: () => navigate('/projects'), keywords: ['work', 'active', 'jobs'] },
      { id: 'messages', title: 'Messages', icon: <MessageSquare className="w-4 h-4 text-emerald-500" />, action: () => navigate('/messages'), keywords: ['chat', 'help', 'communication'] },
      { id: 'invoices', title: 'My Invoices', icon: <DollarSign className="w-4 h-4 text-green-500" />, action: () => navigate('/billing'), keywords: ['billing', 'payments', 'money', 'financials'] },
      { id: 'notifications', title: 'Notifications', icon: <Bell className="w-4 h-4 text-amber-500" />, action: () => navigate('/notifications'), keywords: ['alerts', 'updates', 'unread', 'ping'] },
      { id: 'feedback', title: 'Give Feedback', icon: <Star className="w-4 h-4 text-yellow-500" />, action: () => navigate('/feedback'), keywords: ['review', 'rate', 'experience'] },
    ];

    if (isAdmin) {
      // Admin Main Navigation
      baseActions.push({ id: 'new-proj', title: 'Create Project', subtitle: 'Start a new client mission', icon: <Plus className="w-4 h-4 text-emerald-500" />, action: () => navigate('/create-project'), keywords: ['add', 'new', 'start'] });
      baseActions.push({ id: 'all-proj', title: 'All Projects', subtitle: 'Manage all work', icon: <Briefcase className="w-4 h-4 text-indigo-500" />, action: () => navigate('/admin-projects'), keywords: ['list', 'jobs', 'pipeline'] });
      baseActions.push({ id: 'users', title: 'Manage Users', subtitle: 'Team & Client management', icon: <User className="w-4 h-4 text-amber-500" />, action: () => navigate('/admin-users'), keywords: ['team', 'staff', 'clients', 'management', 'roster'] });
      baseActions.push({ id: 'team', title: 'Team Management', icon: <Users className="w-4 h-4 text-blue-500" />, action: () => navigate('/admin-team'), keywords: ['employees', 'staff', 'collaboration'] });
      
      // Admin Tools
      baseActions.push({ id: 'admin-msg', title: 'Client Messages', icon: <MessageSquare className="w-4 h-4 text-emerald-500" />, action: () => navigate('/admin-messages'), keywords: ['chat', 'support', 'inbox'] });
      baseActions.push({ id: 'admin-notif', title: 'Send Notifications', icon: <Bell className="w-4 h-4 text-amber-500" />, action: () => navigate('/admin-notifications'), keywords: ['broadcast', 'alert', 'push', 'ping'] });
      baseActions.push({ id: 'admin-feedback', title: 'Manage Feedback', icon: <Star className="w-4 h-4 text-yellow-500" />, action: () => navigate('/admin-feedback'), keywords: ['reviews', 'testimonials', 'ratings'] });
      baseActions.push({ id: 'admin-billing', title: 'Billing Management', icon: <DollarSign className="w-4 h-4 text-green-500" />, action: () => navigate('/admin-billing'), keywords: ['invoices', 'payments', 'money', 'revenue'] });
      baseActions.push({ id: 'admin-portfolio', title: 'Portfolio Manager', icon: <BarChart3 className="w-4 h-4 text-violet-500" />, action: () => navigate('/admin-portfolio'), keywords: ['projects', 'case studies', 'display'] });
      
      // AI Tools
      baseActions.push({ id: 'admin-ai', title: 'AI Lead Intelligence', icon: <Target className="w-4 h-4 text-fuchsia-500" />, action: () => navigate('/admin-ai'), keywords: ['leads', 'prospects', 'analysis'] });
      baseActions.push({ id: 'admin-proposals', title: 'AI Proposals', icon: <FolderOpen className="w-4 h-4 text-cyan-500" />, action: () => navigate('/admin-proposals'), keywords: ['quotes', 'bids', 'documents'] });
      
      baseActions.push({ id: 'settings', title: 'Platform Settings', icon: <Settings className="w-4 h-4 text-gray-500" />, action: () => navigate('/admin-settings'), keywords: ['config', 'brand', 'options'] });
      
      // Also add client tools for Admins so they can search "Client Side" pages
      baseActions.push(...clientTools.map(t => ({ ...t, subtitle: 'Client View' })));
    } else {
      // Only Client Tools for regular users
      baseActions.push(...clientTools);
    }

    baseActions.push({ id: 'logout', title: 'Logout', icon: <LogOut className="w-4 h-4 text-red-500" />, action: () => { logout(); setIsOpen(false); }, keywords: ['signout', 'exit', 'quit'] });

    return baseActions;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(results.length, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + Math.max(results.length, 1)) % Math.max(results.length, 1));
    } else if (e.key === 'Enter' && results.length > 0) {
      e.preventDefault();
      executeAction(results[selectedIndex]);
    }
  };

  const executeAction = (item) => {
    item.action();
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100000] flex items-start justify-center pt-[10vh] sm:pt-[20vh] px-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/40 dark:bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={() => setIsOpen(false)}
      />

      {/* Modal */}
      <div className="relative w-full max-w-xl bg-white dark:bg-onyx-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-onyx-800 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Search Input */}
        <div className="flex items-center px-4 py-4 border-b border-slate-100 dark:border-onyx-800">
          <Search className="w-5 h-5 text-slate-400 dark:text-gray-500 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            className="flex-1 bg-transparent border-none outline-none px-4 text-lg text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500"
            placeholder="Type a command or search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <div className="flex items-center gap-1 shrink-0">
            <kbd className="hidden sm:inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black uppercase text-slate-400 dark:text-gray-500 bg-slate-100 dark:bg-onyx-800 border border-slate-200 dark:border-onyx-700">
              ESC
            </kbd>
            <button
              onClick={() => setIsOpen(false)}
              className="sm:hidden p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-gray-300 hover:bg-slate-100 dark:hover:bg-onyx-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto overscroll-contain py-2 bg-slate-50/50 dark:bg-onyx-900/50">
          {results.length > 0 ? (
            <div className="px-2">
              <div className="px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-gray-500">
                {query.length > 0 ? 'Search Results' : 'Suggested Actions'}
              </div>
              
              <ul className="space-y-1 pb-2">
                {results.map((item, index) => (
                  <li key={item.id}>
                    <button
                      onClick={() => executeAction(item)}
                      onMouseEnter={() => setSelectedIndex(index)}
                      className={`w-full flex items-center px-3 py-3 rounded-xl transition-all ${
                        index === selectedIndex
                          ? 'bg-amber-500 text-white shadow-md'
                          : 'hover:bg-slate-100 dark:hover:bg-onyx-800 text-slate-700 dark:text-gray-300'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mr-3 ${
                        index === selectedIndex ? 'bg-white/20 text-white' : 'bg-white dark:bg-onyx-950 shadow-sm'
                      }`}>
                        {React.cloneElement(item.icon, { 
                          className: `w-4 h-4 ${index === selectedIndex ? 'text-white' : ''}` 
                        })}
                      </div>
                      <div className="flex flex-col items-start min-w-0">
                        <span className={`text-sm font-bold truncate ${index === selectedIndex ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                          {item.title}
                        </span>
                        {item.subtitle && (
                          <span className={`text-[10px] uppercase tracking-wide truncate ${index === selectedIndex ? 'text-amber-100' : 'text-slate-400 dark:text-gray-500'}`}>
                            {item.subtitle}
                          </span>
                        )}
                      </div>
                      
                      {index === selectedIndex && (
                          <span className="ml-auto flex shrink-0 items-center justify-center w-6 h-6 rounded-md bg-amber-600/30">
                             <Command className="w-3.5 h-3.5 text-white/70" />
                          </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="py-12 px-4 text-center">
              <div className="w-12 h-12 bg-slate-100 dark:bg-onyx-800 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Search className="w-6 h-6 text-slate-400 dark:text-gray-500" />
              </div>
              <p className="text-sm font-bold text-slate-900 dark:text-white">No results found</p>
              <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">Try a different search term</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandCenter;
